import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Chip,
  Stack
} from '@mui/material';
import {
  ArrowBack,
  Schedule,
  AddCircleOutline,
  CalendarMonth,
  Checklist,
  Widgets,
  Language
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { SITE_ORIGIN, usePageSeo } from './usePageSeo';

const PAGE_TITLE = 'TimeDraw | Clockface Calendar & Reminders for iPhone';
const PAGE_DESCRIPTION = 'TimeDraw turns your calendar and reminders into a clockface you can read at a glance. Events before noon sit inside the clock, events after noon sit outside - a whole new way to see your day.';
const PAGE_IMAGE = `${SITE_ORIGIN}/timedraw/main-view.png`;
const APP_ICON = 'https://is4-ssl.mzstatic.com/image/thumb/Purple116/v4/74/cc/22/74cc2235-8f80-3343-fb60-88ee052921dd/AppIcon-1x_U007emarketing-0-10-0-85-220.png/230x0w.png';

const features = [
  {
    icon: <Schedule />,
    title: 'A clock, not a list',
    body: 'Events and reminders are drawn directly onto an analog clockface. Morning items sit inside the ring, afternoon and evening items sit outside, so a glance tells you where the day stands.'
  },
  {
    icon: <AddCircleOutline />,
    title: 'Add without leaving the clock',
    body: 'Create an event or reminder from a floating composer on the main screen - set recurrence, add notes, and pick a calendar in a few taps.'
  },
  {
    icon: <CalendarMonth />,
    title: 'A default calendar of your choice',
    body: 'Choose which calendar new events and reminders land in when you do not pick one explicitly, instead of falling back to whatever iOS considers the default.'
  },
  {
    icon: <Checklist />,
    title: 'Reminders scoped to today',
    body: 'Reminders show up alongside the day’s events, filtered to what is actually due - with a setting to see every reminder again when you want the full list.'
  },
  {
    icon: <Widgets />,
    title: 'On the Home Screen and the wrist',
    body: 'A Home Screen widget keeps the clockface visible without opening the app, and a watchOS companion mirrors it on your wrist.'
  },
  {
    icon: <Language />,
    title: 'Built for more than English',
    body: 'The full app is localized into English, German, Spanish, and French, including grammatically correct plurals for every count shown on screen.'
  }
];

const TimeDraw = () => {
  const navigate = useNavigate();

  usePageSeo({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    path: '/TimeDraw',
    image: PAGE_IMAGE,
    keywords: 'TimeDraw, clock calendar app, analog clock calendar, iPhone calendar app, reminders app, clockface, visualize your day, iOS calendar, Apple Watch calendar',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'TimeDraw',
      operatingSystem: 'iOS',
      applicationCategory: 'ProductivityApplication',
      description: PAGE_DESCRIPTION,
      url: `${SITE_ORIGIN}/TimeDraw`,
      image: PAGE_IMAGE,
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      },
      author: {
        '@type': 'Person',
        name: 'Michael R. Ellis',
        jobTitle: 'iOS Software Engineer',
        url: `${SITE_ORIGIN}/`
      }
    }
  });

  return (
    <>
      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 5 } }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 3 }}
        >
          Back to Portfolio
        </Button>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: '24px',
            backgroundColor: 'white',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            '@media (prefers-color-scheme: dark)': {
              backgroundColor: '#1a1a1a',
              boxShadow: '0 8px 32px rgba(255,255,255,0.08)'
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'center', md: 'center' },
              gap: { xs: 4, md: 6 }
            }}
          >
            <Box sx={{ flex: '1 1 auto', minWidth: 0, textAlign: { xs: 'center', md: 'left' } }}>
              <Box
                component="img"
                src={APP_ICON}
                alt="TimeDraw app icon"
                sx={{
                  width: { xs: 72, sm: 88 },
                  height: { xs: 72, sm: 88 },
                  borderRadius: '22px',
                  mb: 2
                }}
              />
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.85rem', sm: '2.4rem' },
                  color: '#2c3e50',
                  '@media (prefers-color-scheme: dark)': { color: '#ffffff' }
                }}
              >
                TimeDraw
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mt: 1.5,
                  fontWeight: 500,
                  color: 'text.secondary',
                  '@media (prefers-color-scheme: dark)': { color: '#b0b0b0' }
                }}
              >
                Visualize your time
              </Typography>
              <Typography
                sx={{
                  maxWidth: 560,
                  mt: 2,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  lineHeight: 1.7,
                  color: 'text.secondary',
                  '@media (prefers-color-scheme: dark)': { color: '#c8c8c8' }
                }}
              >
                TimeDraw draws your calendar events and reminders around an analog clockface,
                so your whole day reads at a glance instead of scrolling through a list.
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                justifyContent={{ xs: 'center', md: 'flex-start' }}
                sx={{ mt: 3 }}
              >
                {['iOS', 'Apple Watch', 'Widgets', 'Free'].map((label) => (
                  <Chip
                    key={label}
                    label={label}
                    sx={{
                      fontWeight: 600,
                      backgroundColor: '#e8ecf4',
                      color: '#4a5366',
                      '@media (prefers-color-scheme: dark)': {
                        backgroundColor: '#333',
                        color: '#eee'
                      }
                    }}
                  />
                ))}
              </Stack>
              <Button
                component="a"
                href="https://apps.apple.com/us/app/timedraw/id1603772416"
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                sx={{
                  mt: 3,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: '#2c3e50',
                  '&:hover': { backgroundColor: '#1a2531' }
                }}
              >
                Download on the App Store
              </Button>
            </Box>
            <Box sx={{ flex: '0 0 auto', display: 'flex', justifyContent: 'center' }}>
              <Box
                component="img"
                src="/timedraw/main-view.png"
                alt="TimeDraw's clockface view, showing a day's events and reminders drawn around an analog clock"
                width={300}
                height={400}
                decoding="async"
                sx={{
                  width: '100%',
                  maxWidth: 320,
                  height: 'auto',
                  display: 'block',
                  borderRadius: '16px',
                  boxShadow: '0 16px 48px rgba(0,0,0,0.28)'
                }}
              />
            </Box>
          </Box>
        </Paper>

        <Typography
          variant="h4"
          component="h2"
          sx={{
            mt: { xs: 5, sm: 7 },
            mb: 3,
            textAlign: 'center',
            fontWeight: 700,
            fontSize: { xs: '1.6rem', sm: '2rem' },
            color: '#2c3e50',
            '@media (prefers-color-scheme: dark)': { color: '#ffffff' }
          }}
        >
          What it does
        </Typography>
        <Grid container spacing={2.5}>
          {features.map((feature) => (
            <Grid item key={feature.title} xs={12} sm={6} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: '20px',
                  backgroundColor: 'white',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                  '@media (prefers-color-scheme: dark)': {
                    backgroundColor: '#1a1a1a',
                    boxShadow: '0 8px 32px rgba(255,255,255,0.06)'
                  }
                }}
              >
                <Box sx={{ color: '#4a5366', mb: 1.5, '@media (prefers-color-scheme: dark)': { color: '#a5b4fc' } }}>
                  {feature.icon}
                </Box>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    color: '#2c3e50',
                    '@media (prefers-color-scheme: dark)': { color: '#ffffff' }
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.6,
                    '@media (prefers-color-scheme: dark)': { color: '#b0b0b0' }
                  }}
                >
                  {feature.body}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Typography
          variant="h4"
          component="h2"
          sx={{
            mt: { xs: 5, sm: 7 },
            mb: 3,
            textAlign: 'center',
            fontWeight: 700,
            fontSize: { xs: '1.6rem', sm: '2rem' },
            color: '#2c3e50',
            '@media (prefers-color-scheme: dark)': { color: '#ffffff' }
          }}
        >
          A look inside
        </Typography>
        <Grid container spacing={2.5} justifyContent="center">
          {[
            { src: '/timedraw/main-view.png', alt: 'TimeDraw main clockface view with events and reminders for the day' },
            { src: '/timedraw/add-item.png', alt: 'Adding a new event or reminder in TimeDraw' },
            { src: '/timedraw/busy-day.png', alt: 'TimeDraw clockface showing a busy day with many overlapping events' },
            { src: '/timedraw/settings.png', alt: 'TimeDraw settings, including default calendars and reminder filtering' }
          ].map((shot) => (
            <Grid item key={shot.src} xs={6} sm={3}>
              <Box
                component="img"
                src={shot.src}
                alt={shot.alt}
                loading="lazy"
                decoding="async"
                sx={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  borderRadius: '14px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.18)'
                }}
              />
            </Grid>
          ))}
        </Grid>

        <Paper
          elevation={0}
          sx={{
            mt: { xs: 4, sm: 6 },
            p: { xs: 3, sm: 4 },
            borderRadius: '20px',
            textAlign: 'center',
            backgroundColor: 'white',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            '@media (prefers-color-scheme: dark)': {
              backgroundColor: '#1a1a1a',
              boxShadow: '0 8px 32px rgba(255,255,255,0.06)'
            }
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 700,
              mb: 1.5,
              color: '#2c3e50',
              '@media (prefers-color-scheme: dark)': { color: '#ffffff' }
            }}
          >
            Support
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 2, '@media (prefers-color-scheme: dark)': { color: '#b0b0b0' } }}>
            Questions about the app:{' '}
            <Box
              component="a"
              href="mailto:Contact@MichaelRobertEllis.com"
              sx={{
                color: '#4a5366',
                '@media (prefers-color-scheme: dark)': { color: '#a5b4fc' }
              }}
            >
              Contact@MichaelRobertEllis.com
            </Box>
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="center" alignItems="center">
            <Button component={RouterLink} to="/timedraw-privacy-policy">
              Privacy Policy
            </Button>
          </Stack>
        </Paper>
      </Container>
    </>
  );
};

export default TimeDraw;
